import { zodResolver } from '@hookform/resolvers/zod';
import { useState, type ChangeEvent, type ReactNode } from 'react';
import { useForm, type FieldErrors, type UseFormRegister } from 'react-hook-form';

import { Button } from '../components/ui/Button';
import { createLead } from '../firebase/leadsService';
import { contactSchema, type ContactSchema } from '../lib/schemas/contact';
import { PRACTICES } from '../types/domain';

type Status =
  | { state: 'idle' | 'submitting' | 'success'; error: null }
  | { state: 'error'; error: string };

const DEFAULT_VALUES: ContactSchema = {
  nombre: '',
  empresa: '',
  email: '',
  telefono: '',
  practica: PRACTICES[0],
  mensaje: '',
  acepto: true as const, // Zod literal(true) — RHF necesita el tipo exacto
};

export default function ContactForm() {
  const [status, setStatus] = useState<Status>({ state: 'idle', error: null });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ContactSchema>({
    resolver: zodResolver(contactSchema),
    mode: 'onTouched',
    // shouldFocusError es true por default → al fallar el submit, RHF mueve el foco al primer error.
    defaultValues: { ...DEFAULT_VALUES, acepto: false as unknown as true },
  });

  const onSubmit = handleSubmit(async (data) => {
    setStatus({ state: 'submitting', error: null });
    try {
      await createLead({
        nombre: data.nombre.trim(),
        empresa: data.empresa.trim(),
        email: data.email.trim().toLowerCase(),
        telefono: data.telefono.trim() || null,
        practica: data.practica,
        mensaje: data.mensaje.trim(),
      });
      setStatus({ state: 'success', error: null });
      reset({ ...DEFAULT_VALUES, acepto: false as unknown as true });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'No fue posible enviar el mensaje. Intenta de nuevo.';
      setStatus({ state: 'error', error: message });
    }
  });

  return (
    <section
      id="contacto"
      className="on-blue bg-etribe-blue text-white py-[120px] relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 70% at 0% 100%, rgba(255,200,54,.08), transparent 60%)',
        }}
      />

      <div className="container-etribe relative">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <ContactInfo />

          <div className="bg-white rounded-[20px] p-8 sm:p-12 text-etribe-ink shadow-[0_24px_80px_rgba(10,0,102,.4)]">
            {status.state === 'success' && (
              <div
                role="status"
                className="bg-etribe-green/15 text-[#2D7C20] px-4 py-3.5 rounded-md text-sm mb-4"
              >
                ✔ Mensaje enviado. Te contactaremos en menos de 24 hrs.
              </div>
            )}
            {status.state === 'error' && (
              <div
                role="alert"
                className="bg-etribe-red/10 text-etribe-red px-4 py-3.5 rounded-md text-sm mb-4"
              >
                {status.error}
              </div>
            )}

            <form onSubmit={onSubmit} noValidate>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <Field id="nombre" label="Nombre completo *" placeholder="Tu nombre"
                  register={register} errors={errors} />
                <Field id="empresa" label="Empresa *" placeholder="Nombre de tu empresa"
                  register={register} errors={errors} />
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <Field id="email" type="email" label="Correo corporativo *"
                  placeholder="nombre@empresa.com" register={register} errors={errors} />
                <Field
                  id="telefono"
                  type="tel"
                  label="Teléfono (opcional)"
                  placeholder="10 dígitos"
                  register={register}
                  errors={errors}
                  inputMode="numeric"
                  maxLength={10}
                  formatValue={(v) => v.replace(/\D/g, '').slice(0, 10)}
                />
              </div>

              <div className="flex flex-col gap-1.5 mb-4">
                <label htmlFor="practica" className="text-[12px] font-semibold tracking-[0.08em] uppercase text-etribe-ink-2">
                  Práctica de interés *
                </label>
                <select
                  id="practica"
                  {...register('practica')}
                  aria-invalid={Boolean(errors.practica)}
                  className={`input ${errors.practica ? 'border-etribe-red' : ''}`}
                >
                  {PRACTICES.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
                {errors.practica && <FieldError>{errors.practica.message}</FieldError>}
              </div>

              <div className="flex flex-col gap-1.5 mb-4">
                <label htmlFor="mensaje" className="text-[12px] font-semibold tracking-[0.08em] uppercase text-etribe-ink-2">
                  ¿Qué reto quieres conversar? *
                </label>
                <textarea
                  id="mensaje"
                  rows={3}
                  {...register('mensaje')}
                  placeholder="Cuéntanos el contexto. Entre más concreto, mejor."
                  aria-invalid={Boolean(errors.mensaje)}
                  className={`input resize-y min-h-[100px] ${errors.mensaje ? 'border-etribe-red' : ''}`}
                />
                {errors.mensaje && <FieldError>{errors.mensaje.message}</FieldError>}
              </div>

              <label className="flex items-start gap-2.5 text-[13px] text-etribe-ink-2 mb-2 leading-[1.5]">
                <input
                  id="acepto"
                  type="checkbox"
                  {...register('acepto')}
                  className="w-4 h-4 mt-0.5 accent-etribe-blue"
                />
                <span>
                  Acepto el{' '}
                  <a href="#aviso" className="text-etribe-blue underline">
                    Aviso de Privacidad
                  </a>{' '}
                  y el tratamiento de mis datos para fines comerciales.
                </span>
              </label>
              {errors.acepto && <FieldError className="mb-3">{errors.acepto.message}</FieldError>}

              <Button
                type="submit"
                variant="accent"
                size="lg"
                fullWidth
                disabled={isSubmitting || !isValid}
                isLoading={isSubmitting}
                className="mt-3"
              >
                {isSubmitting ? 'Enviando…' : 'Enviar mensaje'}
              </Button>

              {isValid ? null : Object.keys(errors).length > 0 ? (
                <p className="text-[12px] text-etribe-red mt-3 text-center" role="alert">
                  Revisa los campos marcados arriba.
                </p>
              ) : (
                <p className="text-[12px] text-etribe-ink-2/70 mt-3 text-center">
                  Completa los campos obligatorios para enviar.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

type RegistrableField = Exclude<keyof ContactSchema, 'practica' | 'mensaje' | 'acepto'>;

interface FieldProps {
  id: RegistrableField;
  label: string;
  type?: 'text' | 'email' | 'tel';
  placeholder?: string;
  register: UseFormRegister<ContactSchema>;
  errors: FieldErrors<ContactSchema>;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
  maxLength?: number;
  /** Transformación aplicada al valor antes de que RHF lo registre (p. ej. filtrar no-dígitos). */
  formatValue?: (raw: string) => string;
}

function Field({
  id,
  label,
  type = 'text',
  placeholder,
  register,
  errors,
  inputMode,
  maxLength,
  formatValue,
}: FieldProps) {
  const error = errors[id]?.message;
  const registration = formatValue
    ? register(id, {
        onChange: (e: ChangeEvent<HTMLInputElement>) => {
          e.target.value = formatValue(e.target.value);
        },
      })
    : register(id);
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[12px] font-semibold tracking-[0.08em] uppercase text-etribe-ink-2">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        {...registration}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`input ${error ? 'border-etribe-red' : ''}`}
      />
      {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
    </div>
  );
}

interface FieldErrorProps {
  id?: string;
  className?: string;
  children: ReactNode;
}

function FieldError({ id, className = '', children }: FieldErrorProps) {
  return (
    <p id={id} role="alert" className={`text-[12px] text-etribe-red ${className}`}>
      {children}
    </p>
  );
}



function ContactInfo() {
  return (
    <div>
      <p className="eyebrow"><span>Hablemos</span></p>
      <h2 className="text-[clamp(40px,5vw,64px)] text-white mb-6">Hablemos de tu reto</h2>
      <p className="text-[clamp(17px,1.3vw,20px)] text-white/85 mb-12 max-w-[50ch] leading-[1.55]">
        Cuéntanos el contexto, el riesgo o la decisión que necesitas resolver. Evaluamos contigo si tiene
        sentido avanzar — sin discurso comercial.
      </p>
    </div>
  );
}
